import { ArrowRight, Sparkles, Gift, Star, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

const ResetGiftPage = () => {
  const navigate = useNavigate();

  // Countdown to retreat: June 26, 2026
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const target = new Date('2026-06-26T00:00:00').getTime();
    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center" style={{ backgroundColor: '#1A3C34' }}>
        <div className="absolute inset-0">
          <img
            src="https://i.ontraport.com/260712.dbe554751ad634586f9726bdc420b455.PNG"
            alt="Just Be Retreat"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,60,52,0.5) 0%, rgba(26,60,52,0.92) 100%)' }} />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10 py-20">
          <p className="text-[11px] uppercase tracking-[6px] mb-8" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
            The Just Be Retreat &middot; Costa Rica 2026
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.2] mb-6" data-testid="hero-headline">
            A Reset for the Woman<br />Who Carries So Much
          </h1>
          <div className="w-16 h-[1px] mx-auto mb-8" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-lg lg:text-xl text-white/70 max-w-xl mx-auto leading-relaxed mb-3" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            One woman will receive a fully gifted retreat experience &mdash; made possible by <strong className="text-white/90">Caring For Others</strong>.
          </p>
          <p className="text-base text-white/50 max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            A few others will be supported through scholarship access.
          </p>
          <div className="mt-10">
            <button
              onClick={() => navigate('/reset-gift/apply')}
              className="inline-flex items-center gap-2 text-white font-medium px-10 py-4 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide shadow-lg"
              style={{ backgroundColor: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
              data-testid="hero-apply-btn"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Countdown to Retreat */}
          <div className="mt-12" data-testid="retreat-countdown">
            <div className="mb-5">
              <p className="text-2xl sm:text-3xl text-white font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>
                Just Be:
              </p>
              <p className="text-2xl sm:text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>
                <span style={{ color: '#C06C54' }}>Soft Power</span><span className="text-white text-lg align-super">&trade;</span> <span className="text-white">Retreat Begins In</span>
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 sm:gap-5">
              {[
                { val: countdown.days, label: 'Days' },
                { val: countdown.hours, label: 'Hours' },
                { val: countdown.minutes, label: 'Min' },
                { val: countdown.seconds, label: 'Sec' },
              ].map((unit, i) => (
                <div key={i} className="text-center">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center mb-1.5"
                    style={{ backgroundColor: 'rgba(192,108,84,0.15)', border: '1px solid rgba(192,108,84,0.25)' }}
                  >
                    <span className="text-2xl sm:text-3xl font-light text-white">{String(unit.val).padStart(2, '0')}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40" style={{ fontFamily: "'Manrope', sans-serif" }}>{unit.label}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-white/50" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              Application deadline: <strong className="text-white/80">May 1, 2026</strong>
            </p>
          </div>
        </div>
      </section>

      {/* OPENING — Do You Know Her? */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-2xl sm:text-3xl font-light leading-snug mb-8" style={{ color: '#1A3C34' }}>
                There's a woman this was created for.
              </p>
              <div className="space-y-1 mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                <p className="text-base leading-[2]">She's over 40.</p>
                <p className="text-base leading-[2]">She's lived.</p>
                <p className="text-base leading-[2]">She's led.</p>
                <p className="text-base leading-[2]">She's carried more than most people will ever fully see.</p>
              </div>
              <div className="space-y-1 mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                <p className="text-base leading-[2]">She shows up.</p>
                <p className="text-base leading-[2]">She handles things.</p>
                <p className="text-base leading-[2]">She keeps everything moving.</p>
              </div>
              <p className="text-base leading-[1.8] mb-3" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                And while she does it well&hellip;<br />
                <em>it comes with weight.</em>
              </p>
              <div className="space-y-1 mb-10" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: 'rgba(26,60,52,0.6)' }}>
                <p className="text-base leading-[2]">Not weakness.</p>
                <p className="text-base leading-[2]">Not burnout.</p>
                <p className="text-base leading-[2]">Just&hellip; years of being the one others rely on.</p>
              </div>
              <div className="w-10 h-[1px] mb-8" style={{ backgroundColor: '#C06C54' }} />
              <p className="text-base italic mb-2" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                Pause for a moment &mdash;
              </p>
              <p className="text-xl sm:text-2xl font-semibold" style={{ color: '#C06C54' }}>
                Do you know her?<br />Or is she <em>you</em>?
              </p>
            </div>
            {/* "Is SHE You" Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/kb43z4we_Is%20SHE%20You.jpeg"
                  alt="Is SHE You — A woman reflecting"
                  className="w-full h-auto object-cover"
                  data-testid="is-she-you-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-20" style={{ backgroundColor: '#C06C54' }} />
              <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full opacity-10" style={{ backgroundColor: '#1A3C34' }} />
            </div>
          </div>
        </div>
      </section>

      {/* MISSION — Caring For Others */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          {/* Caring For Others Logo */}
          <div className="mb-10">
            <a href="https://www.caring4others.org/" target="_blank" rel="noopener noreferrer">
              <img
                src="https://images.squarespace-cdn.com/content/v1/694f897ef3caf1215bbcad75/c132fddd-e8cc-4641-a6ca-d8ef3888e2b6/caring-for-others-inc-horizontal-tagline-full-color-rgb-900px-w-72ppi.png?format=1500w"
                alt="Caring For Others, Inc."
                className="mx-auto h-16 sm:h-20 object-contain rounded-lg bg-white/95 px-6 py-3"
                data-testid="cfo-logo"
              />
            </a>
          </div>
          <p className="text-base text-white/60 leading-[1.8] mb-6" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            This gift was made possible through the generosity of <strong className="text-white/90">Caring For Others</strong> &mdash; an organization rooted in the belief that real change happens through care, action, and community impact.
          </p>
          <p className="text-lg text-white/80 leading-relaxed mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            Because they understand something powerful:
          </p>
          <p className="text-2xl sm:text-3xl font-light text-white leading-snug mb-12">
            When a woman is nurtured,<br />she nurtures everything connected to her.
          </p>

          {/* Redesigned "Her family / work / relationships / community" */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { label: 'Her Family', icon: '01', img: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?w=400&q=80' },
              { label: 'Her Work', icon: '02', img: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=400&q=80' },
              { label: 'Her Relationships', icon: '03', img: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&q=80' },
              { label: 'Her Community', icon: '04', img: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=400&q=80' },
            ].map((item, i) => (
              <div
                key={i}
                className="relative rounded-xl overflow-hidden transition-transform duration-300 hover:-translate-y-1 group"
                style={{ border: '1px solid rgba(192,108,84,0.2)' }}
              >
                <div className="h-28 overflow-hidden">
                  <img src={item.img} alt={item.label} className="w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-300" />
                  <div className="absolute inset-0 h-28" style={{ background: 'linear-gradient(to bottom, rgba(26,60,52,0.3) 0%, rgba(26,60,52,0.85) 100%)' }} />
                </div>
                <div className="absolute top-2 left-3">
                  <span
                    className="text-[10px] font-semibold tracking-widest"
                    style={{ color: 'rgba(192,108,84,0.7)', fontFamily: "'Manrope', sans-serif" }}
                  >
                    {item.icon}
                  </span>
                </div>
                <div className="absolute top-0 left-0 right-0 h-28 flex items-center justify-center">
                  <p className="text-base sm:text-lg font-light text-white text-center leading-tight px-2">
                    {item.label}
                  </p>
                </div>
                <div className="w-8 h-[2px] mx-auto my-3" style={{ backgroundColor: '#C06C54' }} />
              </div>
            ))}
          </div>

          <div className="w-16 h-[1px] mx-auto mb-8" style={{ backgroundColor: 'rgba(192,108,84,0.3)' }} />
          <p className="text-base text-white/50 italic mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            This is not just about one woman.<br />
            It's about what happens <strong className="text-white/70">because of her</strong>.
          </p>

          {/* Learn More Link */}
          <a
            href="https://www.caring4others.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 hover:opacity-80"
            style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
            data-testid="cfo-learn-more"
          >
            Learn more about Caring For Others <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* THE INVITATION */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-5xl mx-auto px-6">
          {/* Dome Image Gallery */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14">
            <div className="rounded-2xl overflow-hidden shadow-lg sm:col-span-2 aspect-[16/9]">
              <img
                src="https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/eif7vw9m_geodesic%20dome.jpeg"
                alt="Sacred Geodesic Dome at the Just Be Retreat"
                className="w-full h-full object-cover"
                data-testid="dome-image-main"
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl overflow-hidden shadow-lg flex-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/kr4w8q25_4%20%281%29.png"
                  alt="A woman breathing freely in nature"
                  className="w-full h-full object-cover"
                  data-testid="retreat-woman-image"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg flex-1">
                <img
                  src="https://i.ontraport.com/260712.e8246cf334e8430fccff91d4d75d391c.PNG"
                  alt="Luxury accommodation at Imiloa, Costa Rica"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[5px] mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                The Invitation
              </p>
              <p className="text-2xl sm:text-3xl font-light leading-snug mb-6" style={{ color: '#1A3C34' }}>
                We created this experience for that woman.
              </p>
              <div className="space-y-2 mb-8" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                <p className="text-base leading-[1.8]">A space where she doesn't have to lead.</p>
                <p className="text-base leading-[1.8]">Doesn't have to hold everything.</p>
                <p className="text-base leading-[1.8]">Doesn't have to be "on."</p>
              </div>
              <p className="text-base leading-[1.8] mb-6" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: '#1A3C34' }}>
                We will be gifting one woman a fully supported Reset experience at the <strong>Just Be Retreat in Costa Rica</strong>.
              </p>
            </div>
            <div className="rounded-2xl p-8" style={{ backgroundColor: 'white', border: '1px solid rgba(26,60,52,0.08)' }}>
              <div className="flex items-center gap-3 mb-6">
                <Gift className="h-5 w-5" style={{ color: '#C06C54' }} />
                <p className="text-[11px] uppercase tracking-[4px]" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
                  What's Included
                </p>
              </div>
              <div className="space-y-4 mb-6">
                {[
                  'A double occupancy stay in our Sacred Geodesic Dome (2 king beds — your own private bed, shared dome). We will select your roommate — another amazing woman excited to share the experience',
                  'Meals and full retreat experience',
                  'A nurturing environment designed for restoration, clarity, and reconnection'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: '#C06C54' }} />
                    <p className="text-sm leading-relaxed" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>{item}</p>
                  </div>
                ))}
              </div>
              <div className="w-full h-[1px] mb-4" style={{ backgroundColor: 'rgba(26,60,52,0.06)' }} />
              <p className="text-2xl font-semibold mb-1" style={{ color: '#1A3C34' }}>Value: $5,297</p>
              <p className="text-xs" style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
                Fully gifted &middot; 1 woman selected for the full gift &middot; 3 selected for scholarship (partial pay, min 50%)
              </p>
            </div>
          </div>
          <div className="mt-10 space-y-2" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: 'rgba(26,60,52,0.6)' }}>
            <p className="text-base leading-[1.8] italic">This is not a giveaway in the traditional sense.</p>
            <p className="text-base leading-[1.8]">This is a moment of <strong style={{ color: '#1A3C34' }}>recognition</strong>.</p>
            <p className="text-base leading-[1.8]">A moment of <strong style={{ color: '#1A3C34' }}>restoration</strong>.</p>
            <p className="text-base leading-[1.8]">A moment to <strong style={{ color: '#1A3C34' }}>receive</strong>.</p>
          </div>
        </div>
      </section>

      {/* YOUR OPTIONS */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-6">
            <p className="text-base mb-4" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              You don't have to force yourself into one path.
            </p>
            <p className="text-base" style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              Simply choose what aligns with where you are right now:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              {
                icon: Gift,
                title: 'Apply for the Gifted Experience',
                desc: 'For the woman who knows she needs this \u2014 and is open to receiving support.',
                btn: 'Apply for the Gift',
                action: () => navigate('/reset-gift/apply?option=gift'),
                testId: 'cta-gift'
              },
              {
                icon: Star,
                title: 'Explore Scholarship Access',
                desc: 'For those who feel called and are able to invest at a supported level.',
                btn: 'View Scholarship Options',
                action: () => navigate('/reset-gift/apply?option=scholarship'),
                testId: 'cta-scholarship'
              },
              {
                icon: Users,
                title: 'Reserve Your Spot Now',
                desc: "For the woman who's ready to say yes and secure her spot now.",
                subtext: "Your full-pay reservation helps us provide scholarships for other brilliant women who need this experience.",
                btn: 'Reserve Your Spot',
                action: () => navigate('/retreats'),
                testId: 'cta-reserve'
              }
            ].map((opt, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 flex flex-col text-center shadow-sm hover:shadow-lg transition-shadow duration-500" style={{ border: '1px solid rgba(26,60,52,0.06)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#C06C54' }}>
                  <opt.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A3C34' }}>{opt.title}</h3>
                <p className="text-sm leading-relaxed flex-1 mb-6" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, color: 'rgba(26,60,52,0.7)' }}>
                  {opt.desc}
                  {opt.subtext && (
                    <span className="block mt-2 text-xs italic" style={{ color: 'rgba(26,60,52,0.45)' }}>{opt.subtext}</span>
                  )}
                </p>
                <button
                  onClick={opt.action}
                  className="inline-flex items-center justify-center gap-2 text-white font-medium px-6 py-3.5 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide"
                  style={{ backgroundColor: i === 2 ? '#1A3C34' : '#C06C54', fontFamily: "'Manrope', sans-serif" }}
                  data-testid={opt.testId}
                >
                  {opt.btn} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GENTLE REFLECTION CLOSE */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="space-y-2 mb-10">
            <p className="text-lg text-white/60 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              If this has been sitting with you&hellip;
            </p>
            <p className="text-lg text-white/60 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
              if something in you keeps coming back to this&hellip;
            </p>
          </div>
          <p className="text-2xl sm:text-3xl font-light text-white leading-snug mb-8">
            That matters.
          </p>
          <div className="space-y-2 mb-10" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            <p className="text-base text-white/50">You don't have to overthink it.</p>
            <p className="text-base text-white/50">You don't have to justify it.</p>
          </div>
          <p className="text-base text-white/70 leading-relaxed mb-3" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            Just be honest about what you need in this season.
          </p>
          <div className="w-16 h-[1px] mx-auto mb-8" style={{ backgroundColor: 'rgba(192,108,84,0.4)' }} />
          <p className="text-xl sm:text-2xl font-light italic" style={{ color: '#C06C54' }}>
            Because when you are restored&hellip;<br />
            everything connected to you feels it.
          </p>
        </div>
      </section>

      {/* ELIGIBILITY + FINAL CTA */}
      <section className="py-16" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-xs mb-8" style={{ color: 'rgba(26,60,52,0.45)', fontFamily: "'Manrope', sans-serif", fontWeight: 300 }}>
            This experience is designed for women in midlife &mdash; typically 40+ &mdash; who are navigating meaningful seasons of life, leadership, and transition.
          </p>
          <button
            onClick={() => navigate('/reset-gift/apply')}
            className="inline-flex items-center gap-2 text-white font-medium px-10 py-4 rounded-full transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 text-sm tracking-wide shadow-lg"
            style={{ backgroundColor: '#C06C54', fontFamily: "'Manrope', sans-serif" }}
            data-testid="final-apply-btn"
          >
            Begin Your Application <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-10" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-[11px] uppercase tracking-[5px] text-white/40" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 400 }}>
            Sister Diamonds &middot; Caring For Others
          </p>
        </div>
      </section>
    </div>
  );
};

export default ResetGiftPage;
